import React from "react";
import {
  FaCalendarAlt,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaUsers,
  FaUserCheck,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function StatCard({ icon, label, value, colorClass = "", valueClassName = "" }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/60">
      <div
        className={cn(
          "mb-3 flex h-10 w-10 items-center justify-center rounded-xl text-base",
          colorClass
        )}
      >
        {icon}
      </div>

      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p
        className={cn(
          "mt-2 text-xl font-black leading-tight text-slate-900 dark:text-slate-100 sm:text-2xl",
          valueClassName
        )}
      >
        {value}
      </p>
    </div>
  );
}

const AttendancePageHeader = ({
  loading,
  club,
  dojos = [],
  groups = [],
  adherentsCount = 0,
  attendanceDateLabel = "",
  isTeacher = false,
  teacher = null,
  teacherAccessMap = {},
}) => {
  if (loading) {
    return (
      <section className="mt-5 grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div className="animate-pulse overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-24 bg-slate-200 dark:bg-slate-800" />
          <div className="flex items-center gap-3 p-4">
            <div className="h-14 w-14 rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="flex-1">
              <div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-800" />
              <div className="mt-2 h-3 w-40 rounded bg-slate-200 dark:bg-slate-800" />
            </div>
          </div>
        </div>

        <div className="animate-pulse rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
            <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
            <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
            <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
          </div>
          <div className="mt-4 h-14 rounded-2xl bg-slate-200 dark:bg-slate-800" />
        </div>
      </section>
    );
  }

  if (!club) return null;

  const accessibleDojos = Object.values(teacherAccessMap).filter(
    (groupsList) => Array.isArray(groupsList) && groupsList.length > 0
  ).length;

  return (
    <section className="mt-5">
      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        {/* Bloc club compact */}
        <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-blue-900 px-5 py-4 text-white">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">
              Club
            </p>
            <h2 className="mt-2 text-xl font-black leading-tight">
              {club.nom_club}
            </h2>
            <p className="mt-1 text-sm text-white/80">
              {club.departement_club || "Département non renseigné"}
            </p>
          </div>

          <div className="flex items-center gap-3 p-4">
            {club.logo_url ? (
              <img
                src={club.logo_url}
                alt={club.nom_club}
                className="h-16 w-16 rounded-full border-2 border-white object-cover shadow-lg ring-1 ring-slate-200 dark:border-slate-900 dark:ring-slate-700"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500 shadow-lg dark:bg-slate-800 dark:text-slate-300">
                <FaUsers className="text-2xl" />
              </div>
            )}

            <div className="min-w-0 flex-1">
              <p className="line-clamp-3 text-sm leading-5 text-slate-600 dark:text-slate-400">
                {club.adresse_club || "Adresse non renseignée"}
              </p>
            </div>
          </div>
        </div>

        {/* Bloc stats compact */}
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              icon={<FaMapMarkerAlt />}
              label="Lieux"
              value={dojos.length}
              colorClass="bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
            />

            <StatCard
              icon={<FaLayerGroup />}
              label="Groupes"
              value={groups.length}
              colorClass="bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300"
            />

            <StatCard
              icon={<FaUsers />}
              label="Adhérents"
              value={adherentsCount}
              colorClass="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
            />

            <StatCard
              icon={<FaCalendarAlt />}
              label="Séance"
              value={attendanceDateLabel}
              valueClassName="text-lg sm:text-xl xl:text-[1.6rem]"
              colorClass="bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
            />
          </div>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-800/60">
            {isTeacher ? (
              <div className="flex flex-wrap items-center gap-2 text-slate-700 dark:text-slate-300">
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 font-semibold text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                  <FaUserCheck />
                  Accès enseignant
                </span>
                <span>
                  {teacher?.prenom} {teacher?.nom} · {accessibleDojos} lieu(x) ·{" "}
                  {groups.length} groupe(s)
                </span>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2 text-slate-700 dark:text-slate-300">
                <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 font-semibold text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                  <FaUserCheck />
                  Accès complet
                </span>
                <span>
                  Vous pouvez consulter et gérer tous les lieux et groupes du
                  club.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AttendancePageHeader;